// Accounting has no sign-in UI: identity belongs to the Sylviq platform.
// Signed-out visitors are sent to the platform sign-in page (build-time
// VITE_PLATFORM_SIGNIN_URL) and return here via ?return_to=.
import { isFullStackTestMode, isUiFixtureMode } from "@/lib/dev-mode";

const FALLBACK_SIGNIN_URL = "https://www.sylviq.io/login";

export function platformAccountUrl(): string {
  // Fixture mode remains local and has no real platform session to manage.
  if (isUiFixtureMode) return "#sylviq-account";
  const base = isFullStackTestMode
    ? "https://www.sylviq.test:4174/login"
    : import.meta.env.VITE_PLATFORM_SIGNIN_URL || FALLBACK_SIGNIN_URL;
  return new URL("/home", base).toString();
}

export function platformSignInUrl(returnTo: string): string | null {
  if (isUiFixtureMode) return null;
  const base = isFullStackTestMode
    ? "https://www.sylviq.test:4174/login"
    : import.meta.env.VITE_PLATFORM_SIGNIN_URL || FALLBACK_SIGNIN_URL;
  const url = new URL(base);
  url.searchParams.set("return_to", returnTo);
  return url.toString();
}

function consoleOrigin(): string {
  return isFullStackTestMode
    ? "https://www.sylviq.test:4174"
    : new URL(
        import.meta.env.VITE_PLATFORM_SIGNIN_URL || FALLBACK_SIGNIN_URL,
      ).origin;
}

/** Build's installation view is owned by Sylviq Console; Accounting only links to it. */
export function consoleExtensionsUrl(organizationId: string): string | null {
  if (isUiFixtureMode) return null;
  const url = new URL(
    `/organizations/${encodeURIComponent(organizationId)}/extensions`,
    consoleOrigin(),
  );
  url.searchParams.set("tab", "installed");
  url.hash = "installations-heading";
  return url.toString();
}

/** Console owns organization roles and their permission grants. */
export function consoleRolesUrl(organizationId: string): string | null {
  if (isUiFixtureMode) return null;
  const url = new URL(
    `/organizations/${encodeURIComponent(organizationId)}/roles`,
    consoleOrigin(),
  );
  url.hash = "roles-heading";
  return url.toString();
}

/** Console owns the legal-entity directory that Accounting's books attach to. */
export function consoleLegalEntitiesUrl(organizationId: string): string | null {
  if (isUiFixtureMode) return null;
  return new URL(
    `/organizations/${encodeURIComponent(organizationId)}/entities`,
    consoleOrigin(),
  ).toString();
}

export function redirectToPlatformSignIn(): void {
  const signInUrl = platformSignInUrl(window.location.href);
  if (signInUrl) window.location.assign(signInUrl);
}
